import { useState } from "react";
import { Button } from "@/components/_components/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/_components/alert-dialog";
import { Trash2, AlertTriangle } from "lucide-react";
import toast from "react-hot-toast";
import * as api from "@/lib/api/client";
import type { ComplianceCountryDto } from "@/lib/api/types";
import { useErrorHandler } from "@/hooks/useErrorHandler";

interface DeleteComplianceCountryDialogProps {
  country: ComplianceCountryDto;
  onSuccess?: () => void;
}

const DeleteComplianceCountryDialog: React.FC<DeleteComplianceCountryDialogProps> = ({
  country,
  onSuccess
}) => {
  const { handleError } = useErrorHandler();
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleDelete = async () => {
    if (!country.id) return;

    try {
      setIsLoading(true);
      await api.deleteComplianceCountry(country.id);
      toast.success(`${country.countryName} deleted successfully`);
      setOpen(false);
      onSuccess?.();
    } catch (error: unknown) {
      // Use centralized error handler to display appropriate error message
      handleError(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="h-8 w-8 p-0 hover:bg-destructive/10 hover:text-destructive transition-colors" 
          title="Delete Country"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Delete Country
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <p>
              Are you sure you want to delete <strong>{country.countryName}</strong>?
            </p>
            <p className="text-sm text-muted-foreground">
              This action cannot be undone. All compliance and quiet hours settings for this country will be permanently removed.
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={isLoading}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isLoading ? (
              <>
                <Trash2 className="h-4 w-4 mr-2 animate-pulse" />
                Deleting...
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Country
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteComplianceCountryDialog;